import React, { useState, useEffect } from "react";
import { useSelector, useDispatch } from "react-redux";
import { useParams } from "react-router-dom";
import Modal from "common/modal";
import Button from "react-bootstrap/Button";
import deleteIconBlue from "svg/delete_blue.svg";
import editIcon from "svg/edit_blue.svg";
import warning from "svg/warning.svg";
import alertIcon from "svg/red-alert-icon.svg";
import CustomTooltip from "common/tooltip";
import { createOrUpdateCutMeasurements, loadCutSheetData, deleteRollCut } from "store/siteRepo/actionCreator";
import toInteger from "lodash/toInteger";
import AddOrEditRollModal from "./AddOrEditRollModal";
import axios from "services/config";
import { showSpinner, hideSpinner } from "store/slices/spinner";
import { Alert } from "react-bootstrap";

const parseLength = (length = "") => {
  const [feet, inches] = length.split("'");
  return {
    feet: toInteger((feet ?? "").replace(/\D/g, "")),
    inches: toInteger((inches ?? "").replace(/\D/g, "")),
  };
};

const RollCut = ({ projectId, cutSheet, roll, rollCut, territoryAccess }) => {
  const { siteId } = useParams();
  const dispatch = useDispatch();
  const { sub } = useSelector((state) => state.loggedInUser);

  const [showEditModal, setShowEditModal] = useState(false);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [label, setLabel] = useState(rollCut.label);
  const [feet, setFeet] = useState(parseLength(rollCut.length).feet);
  const [inches, setInches] = useState(parseLength(rollCut.length).inches);

  useEffect(() => setLabel(rollCut.label), [rollCut.label]);
  useEffect(() => {
    const { feet, inches } = parseLength(rollCut.length);
    setFeet(feet);
    setInches(inches);
  }, [rollCut.length]);

  const confirmDeleteMeasurement = async () => {
    setShowDeleteModal(false);
    dispatch(deleteRollCut({ siteId, projectId, updatedBy: sub, action: "RollCut", id: rollCut.rollCutId }));
  };

  const confirmEditRollCut = async () => {
    setShowEditModal(false);
    dispatch(showSpinner());
    const editRollCutReqObj = {
      layerCutSheetId: cutSheet.layerCutSheetId,
      projectId,
      productName: cutSheet.productName,
      updatedBy: sub,
      rolls: [
        {
          ...roll,
          rollCuts: [
            {
              rollCutId: rollCut.rollCutId,
              label,
              length: `${toInteger(feet)}'${toInteger(inches)}"`,
              pieceCuts: rollCut.pieceCuts,
            },
          ],
        },
      ],
    };
    await axios.post(`/fc-site-repo/create/cutSheet/roll`, editRollCutReqObj);
    await dispatch(loadCutSheetData(projectId));
    dispatch(hideSpinner());
  };

  return (
    <div className="accordion-item" key={`rollCutId-${rollCut.rollCutId}`}>
      <div className="product-name justify-content-between">
        <span className="product-name-value roll-cut d-flex align-items-center">
          <span>Roll Cut</span>
        </span>
        {territoryAccess && (
          <div className="d-flex d-md-none">
            <Button variant="link" className="p-1" onClick={() => setShowEditModal(true)}>
              <img src={editIcon} alt="..." />
            </Button>
            <Button variant="link" className="p-1 mx-2" onClick={() => setShowDeleteModal(true)}>
              <img src={deleteIconBlue} alt="..." />
            </Button>
          </div>
        )}
      </div>
      <div className="accordion-row d-flex align-items-start flex-column">
        <div className="d-flex align-items-start align-items-md-center flex-column flex-md-row w-100 product-attribute-container border-left-rollcut">
          <div className="d-flex flex-row flex-md-column justify-content-between justify-content-md-start product-attribute sales-number col-1 col-md-3 seperator">
            <span className="label">Label</span>
            <span>{rollCut.label}</span>
          </div>
          <div className="d-flex flex-row flex-md-column justify-content-between justify-content-md-start product-attribute sales-number col-1 col-md-2 seperator">
            <span className="label">Length</span>
            <span>{rollCut.length ? rollCut.length : "NA"}</span>
          </div>
          <div className="d-flex flex-row flex-md-column justify-content-between justify-content-md-start product-attribute sales-number col-1 col-md-2">
            <span className="label">No. Of Piece Cuts</span>
            <span>{rollCut?.pieceCuts?.length ? rollCut?.pieceCuts?.length : 0}</span>
          </div>
          <div className="item justify-content-end expand d-none d-md-flex flex-grow-1">
            {territoryAccess && (
              <>
                <Button variant="link" className="p-1" onClick={() => setShowEditModal(true)}>
                  <img src={editIcon} alt="..." />
                </Button>
                <Button variant="link" className="p-1 ms-4" onClick={() => setShowDeleteModal(true)}>
                  <img src={deleteIconBlue} alt="..." />
                </Button>
              </>
            )}
          </div>
        </div>
      </div>
      {showEditModal && (
        <Modal
          title="Edit Roll Cut"
          handleSave={confirmEditRollCut}
          handleCancel={() => setShowEditModal(false)}
          size="lg"
          closeButtonText={"Cancel"}
          saveButtonText="Save"
          isSaveBtnDisabled={toInteger(feet) === 0 && toInteger(inches) === 0}
        >
          {feet === 0 && inches === 0 && <Alert variant="danger">length must be larger than 0</Alert>}
          <div className="container-fluid border-0 m-0">
            <div className="search-form-container__fields">
              <form className="row search-form-container__fields__form d-flex">
                <label className="search-form-container__fields__form-group d-block" style={{ width: "33%" }}>
                  Label
                  <input
                    className="form-control search-form-container__fields__form-group__item mt-2"
                    id="rollCut-label"
                    type="text"
                    placeholder="Enter Label"
                    value={label}
                    onChange={(e) => setLabel(e.target.value)}
                  />
                </label>
                <label className="search-form-container__fields__form-group d-block" style={{ width: "33%" }}>
                  Feet
                  <input
                    className="form-control search-form-container__fields__form-group__item mt-2"
                    id="feet"
                    type="number"
                    placeholder="Enter Feet"
                    value={feet}
                    onChange={(e) => setFeet(e.target.value === "" ? "" : toInteger(e.target.value.substring(0, 4)))}
                    onKeyDown={(e) => new RegExp("[0-9]|Backspace|Delete").test(e.key) || e.preventDefault()}
                  />
                </label>
                <label className="search-form-container__fields__form-group d-block" style={{ width: "33%" }}>
                  Inches
                  <input
                    className="form-control search-form-container__fields__form-group__item mt-2"
                    id="inches"
                    type="number"
                    placeholder="Enter Inches"
                    value={inches}
                    onChange={(e) => setInches(e.target.value === "" ? "" : Math.min(toInteger(e.target.value), 11))}
                    onKeyDown={(e) => new RegExp("[0-9]|Backspace|Delete").test(e.key) || e.preventDefault()}
                  />
                </label>
              </form>
            </div>
          </div>
        </Modal>
      )}

      {showDeleteModal && (
        <Modal
          title="Delete Measurement"
          handleSave={confirmDeleteMeasurement}
          handleCancel={() => setShowDeleteModal(false)}
          size="lg"
          sectionName={""}
          closeButtonText={"Cancel"}
          saveButtonText={"Delete"}
          saveButtonClass="removeBtnClass"
        >
          <div className="container-fluid p-2 border-0 m-0">
            <p>
              Are you sure you want to delete a <b>RollCut</b> measurement from product <b>{cutSheet.productName}</b>?
            </p>
          </div>
        </Modal>
      )}
    </div>
  );
};

const AddCutRollModal = ({ projectId, setShowAddModal, roll, cutSheet }) => {
  const dispatch = useDispatch();
  const { sub } = useSelector((state) => state.loggedInUser);

  const [label, setLabel] = useState("");
  const [feet, setFeet] = useState("");
  const [inches, setInches] = useState("");

  const confirmAddMeasurement = async () => {
    setShowAddModal(false);
    await dispatch(showSpinner());
    await dispatch(
      createOrUpdateCutMeasurements({
        layerCutSheetId: cutSheet.layerCutSheetId,
        projectId: projectId,
        productName: cutSheet.productName,
        linearLength: cutSheet.linearLength ?? "",
        updatedBy: sub,
        rolls: [
          {
            ...roll,
            rollCuts: [{ rollCutId: "", label, length: `${toInteger(feet)}'${toInteger(inches)}"`, pieceCuts: [] }],
          },
        ],
      }),
    );
    await dispatch(loadCutSheetData(projectId));
    await dispatch(hideSpinner());
  };

  return (
    <Modal
      title="Add Roll Cut"
      handleSave={confirmAddMeasurement}
      handleCancel={() => setShowAddModal(false)}
      size="lg"
      sectionName=""
      closeButtonText={"Cancel"}
      saveButtonText="Save"
      isSaveBtnDisabled={toInteger(feet) === 0 && toInteger(inches) === 0}
    >
      {feet === 0 && inches === 0 && <Alert variant="danger">length must be larger than 0</Alert>}
      <div className="container-fluid p-2 border-0 m-0">
        <div className="search-form-container__fields">
          <form className="search-form-container__fields__form">
            <div className="row align-items-center d-flex">
              <label className="search-form-container__fields__form-group d-block" style={{ width: "33%" }}>
                Label
                <input
                  className="form-control search-form-container__fields__form-group__item mt-2"
                  id="rollCut-label"
                  type="text"
                  placeholder="Enter Label"
                  value={label}
                  onChange={(e) => setLabel(e.target.value)}
                />
              </label>
              <label className="search-form-container__fields__form-group d-block" style={{ width: "33%" }}>
                Feet
                <input
                  className="form-control search-form-container__fields__form-group__item mt-2"
                  id="feet"
                  type="number"
                  placeholder="Enter Feet"
                  value={feet}
                  onChange={(e) => setFeet(e.target.value === "" ? "" : toInteger(e.target.value.substring(0, 4)))}
                  onKeyDown={(e) => new RegExp("[0-9]|Backspace|Delete").test(e.key) || e.preventDefault()}
                />
              </label>
              <label className="search-form-container__fields__form-group d-block" style={{ width: "33%" }}>
                Inches
                <input
                  className="form-control search-form-container__fields__form-group__item mt-2"
                  id="inches"
                  type="number"
                  placeholder="Enter Inches"
                  value={inches}
                  onChange={(e) => setInches(e.target.value === "" ? "" : Math.min(toInteger(e.target.value), 11))}
                  onKeyDown={(e) => new RegExp("[0-9]|Backspace|Delete").test(e.key) || e.preventDefault()}
                />
              </label>
            </div>
          </form>
        </div>
      </div>
    </Modal>
  );
};

const Roll = ({ projectId, product, cutSheet, roll, rollIndex, territoryAccess }) => {
  const dispatch = useDispatch();

  const [showEditModal, setShowEditModal] = useState(false);
  const [showAddModal, setShowAddModal] = useState(false);
  const [showDeleteModal, setShowDeleteModal] = useState(false);

  const { feet, inches } = parseLength(roll.length);
  const rollCutsLengthInInches = (roll?.rollCuts ?? []).reduce((totalInches, rollCut) => {
    const { feet, inches } = parseLength(rollCut.length);
    return totalInches + inches + feet * 12;
  }, 0);

  const rollLengthInInches = inches + feet * 12;

  return (
    <div className="accordion-item parent-roll" key={rollIndex}>
      <div className="product-name justify-content-between">
        <span className="product-name-value d-flex align-items-center">
          <span>Roll</span>
        </span>
        {territoryAccess && (
          <div className="d-flex d-md-none">
            <Button variant="link" className="p-1" onClick={() => setShowEditModal(true)}>
              <img src={editIcon} alt="..." />
            </Button>
            <Button variant="link" className="p-1 mx-3" onClick={() => setShowDeleteModal(true)}>
              <img src={deleteIconBlue} alt="..." />
            </Button>
            <Button variant="outline-primary" className="p-1 roll-mob-btn" onClick={() => setShowAddModal(true)}>
              Add Roll Cut
            </Button>
          </div>
        )}
      </div>
      <div className="accordion-row d-flex align-items-start flex-column">
        <div className="d-flex align-items-start align-items-md-center flex-column flex-md-row w-100 product-attribute-container">
          <div className="d-flex flex-row flex-md-column justify-content-between justify-content-md-start product-attribute sales-number col-1 col-md-3 seperator">
            <span className="label">Product Name</span>
            <span>
              {cutSheet.productName}
              {rollCutsLengthInInches !== rollLengthInInches && roll?.rollCuts?.length > 0 && (
                <CustomTooltip
                  placement="top"
                  trigger="hover"
                  text="Roll Cut lengths do not add up to match Roll length"
                >
                  <img className="alert_style_icon mx-3" src={alertIcon} />
                </CustomTooltip>
              )}
            </span>
          </div>
          <div className="d-flex flex-row flex-md-column justify-content-between justify-content-md-start product-attribute sales-number col-1 col-md-1 seperator">
            <span className="label">Index</span>
            <span>{roll.index ? roll.index : "NA"}</span>
          </div>
          <div className="d-flex flex-row flex-md-column justify-content-between justify-content-md-start product-attribute category col-1 seperator">
            <span className="label">Length</span>
            <span>{roll.length ? roll.length : "NA"}</span>
          </div>
          <div className="d-flex flex-row flex-md-column justify-content-between justify-content-md-start product-attribute category col-2">
            <span className="label">No. Of Roll Cuts</span>
            <span>{roll?.rollCuts?.length ? roll?.rollCuts?.length : 0}</span>
          </div>
          <div className="item justify-content-end expand d-none d-md-flex flex-grow-1">
            {territoryAccess && (
              <>
                <Button variant="link" className="p-1 mt-2" onClick={() => setShowEditModal(true)}>
                  <img src={editIcon} alt="..." />
                </Button>
                <Button variant="link" className="p-1 mx-5 mt-2" onClick={() => setShowDeleteModal(true)}>
                  <img src={deleteIconBlue} alt="..." />
                </Button>
                <Button
                  variant="outline-primary"
                  style={{ whiteSpace: "nowrap" }}
                  onClick={() => setShowAddModal(true)}
                >
                  Add Roll Cut
                </Button>
              </>
            )}
          </div>
        </div>
      </div>
      <div className="d-flex flex-wrap flex-column product-container">
        <div className="py-3 ps-3">
          <div className="accordion-items-container product-view">
            {roll.rollCuts?.length ? (
              roll.rollCuts?.map((rollCut) => (
                <RollCut
                  projectId={projectId}
                  cutSheet={cutSheet}
                  roll={roll}
                  rollCut={rollCut}
                  territoryAccess={territoryAccess}
                />
              ))
            ) : (
              <div className="row">
                <div className="alert d-flex align-items-center" role="alert">
                  <img src={warning} alt={"warning-icon"} />
                  <div>No Roll Cuts available!</div>
                </div>
              </div>
            )}
          </div>
        </div>
      </div>
      {showEditModal && (
        <AddOrEditRollModal
          roll={roll}
          projectId={projectId}
          productsData={[product]}
          setShowAddModal={setShowEditModal}
        />
      )}

      {showAddModal && (
        <AddCutRollModal projectId={projectId} roll={roll} cutSheet={cutSheet} setShowAddModal={setShowAddModal} />
      )}

      {showDeleteModal && (
        <Modal
          title="Delete Measurement"
          handleSave={async () => {
            setShowDeleteModal(false);
            await dispatch(showSpinner());
            await axios.delete(`/fc-site-repo/roll/${roll.rollId}`);
            dispatch(loadCutSheetData(projectId));
            await dispatch(hideSpinner());
          }}
          handleCancel={() => setShowDeleteModal(false)}
          size="lg"
          sectionName=""
          closeButtonText={"Cancel"}
          saveButtonText="Delete"
          saveButtonClass="removeBtnClass"
        >
          <div className="container-fluid p-2 border-0 m-0">
            <p>
              Are you sure you want to delete a <b>Roll</b> measurement from product <b>{cutSheet.productName}</b>?
            </p>
          </div>
        </Modal>
      )}
    </div>
  );
};

const CutMeasurements = ({ cutSheetData, productsData, projectId, territoryAccess }) => {
  const [showAddRollModal, setShowAddRollModal] = useState(false);

  const cutMeasurementsExist = cutSheetData.some((cutSheet) => cutSheet.rolls?.length ?? [] > 0);

  return (
    <div className="container-fluid search-form-container cutsheets-container cuts-measurements">
      <div className="search-form-container__title d-flex justify-content-between align-items-center">
        <div className="d-flex align-items-center">
          <span className="search-form-container__title--text">Cut Measurements</span>
        </div>
        {territoryAccess && (
          <Button className="me-0" variant="outline-primary" onClick={() => setShowAddRollModal(true)}>
            Add Roll
          </Button>
        )}
      </div>
      <div>
        <div className="accordion-items-container product-view">
          {cutMeasurementsExist ? (
            cutSheetData?.map((cutSheet) =>
              cutSheet.rolls?.map((roll, rollIndex) => (
                <Roll
                  projectId={projectId}
                  cutSheet={cutSheet}
                  product={productsData.find((p) => p.id === cutSheet.productName)}
                  roll={roll}
                  rollIndex={rollIndex}
                  territoryAccess={territoryAccess}
                />
              )),
            )
          ) : (
            <div className="row">
              <div className="alert d-flex align-items-center" role="alert">
                <img src={warning} alt={"warning-icon"} />
                <div>No Cut Measurements available in this floor!</div>
              </div>
            </div>
          )}
        </div>
      </div>
      {showAddRollModal && (
        <AddOrEditRollModal projectId={projectId} setShowAddModal={setShowAddRollModal} productsData={productsData} />
      )}
    </div>
  );
};

export default CutMeasurements;
